import type { FieldType } from "@/enums/offers";
import type {
  PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE,
  PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE,
  PLACEMENT_NO_WFB_TEMPLATE,
} from "@/helpers/constants/events";
import type { IApplication, IStip } from "@/models/applications";
import type { IClient } from "@/models/clients";
import type {
  Booleanish,
  FileBase,
  FileSent,
  IPaginatedResponse,
  IResponseMeta,
  placementMethod,
} from "@/models/common";
import type { OfferTemplate, OfferTemplateContent } from "@/models/offers";
import type { ProductType, RiskTier, SubmissionMethod } from "@/models/options";
import type { IScorecardResult } from "@/models/scorecards";
import type { IUser } from "@/models/users";
import type {
  FinProduct,
  IWorkflow,
  WfbFunderRecord,
} from "@/models/workflows";

export interface IProduct {
  id: ProductType;
  name: string;
  funders?: IFunder[];
}

export interface OfferTemplateRecord {
  name?: string;
  id: string;
  product_type?: FinProduct;
  product?: FinProduct;
  offer_template?: OfferTemplateContent;
}

export interface IFunder {
  area_id: number | null;
  available_methods: placementMethod[];
  can_postpone_approval?: boolean;
  allow_submission_on_decline?: boolean;
  city_id: number | null;
  client_id?: IClient["id"];
  country_id: number | null;
  dept_service_coverage: number | null;
  email_addresses: Record<string, string[]>;
  fico_target: number | null;
  focus_industries: number[] | null;
  full_name: string;
  funding_countries: number[] | null;
  high_risk_industries: number[] | null;
  id: string;
  is_approved: boolean;
  is_automated: boolean;
  is_biweekly_payment: boolean;
  is_daily_payment: boolean;
  is_hard_pull_for_funding: boolean;
  is_hard_pull_for_offer: boolean;
  is_home_office_restricted: boolean;
  is_monthly_payment: boolean;
  is_past_bankruptcy_restricted: boolean;
  is_titled_equipment: boolean;
  is_untitled_equipment: boolean;
  is_weekly_payment: boolean;
  max_funding_amount: number | null;
  max_monthly_deposits: number | null;
  max_negative_days: number | null;
  max_negative_days_3_month_avg: number | null;
  max_negative_days_last_month: number | null;
  max_term: number | null;
  min_avg_daily_balance: number | null;
  min_deposits: number | null;
  min_fico: number | null;
  min_funding_amount: number | null;
  min_monthly_deposits: number | null;
  min_monthly_gross?: number;
  min_monthly_revenue: number | null;
  min_num_of_trucks: number | null;
  min_term: number | null;
  min_time_in_business: string;
  monthly_revenue_target: number | null;
  months_in_business_target: number | null;
  months_since_bk_discharge: number | null;
  months_since_bk_dismissed: number | null;
  position_funded: string | null;
  primary_method: placementMethod;
  productType?: string;
  product_types: ProductType[];
  restricted_corporate_structures: number[] | null;
  restricted_industries: number[] | null;
  restricted_states: number[] | null;
  risk_tiers: RiskTier[];
  selected_method?: placementMethod;
  submission?: string;
  score_card_results?: IScorecardResult[];
  website: string | null;
  description: string | null;
  logo: FileBase | null;
  offer_template?: OfferTemplateRecord | null;
  client_offer_template?: string;
  workflow_template_id?: string;
}

export interface IFunderEmailsWithProductTypes {
  email: string;
  types: string[];
  id?: string;
}

/** DEPREICATED: Remove in favor of PlacementStatuses below. */
export type IPlacementStatuses = {
  "1": "Pending";
  "2": "Declined";
  "3": "Approved";
  "4": "Error";
  "5": "Queued";
};

export enum PlacementStatuses {
  Pending = 1,
  Declined,
  Approved,
  Error,
  Queued,
  Duplicate,
  PendingReview,
  PendingMissingInfoOrDocs,
}

export interface ILenderPlacementResponse {
  id: string;
  endpoint: string;
  response: string | Record<string, unknown>;
  created_at: string;
}

export interface ILenderPlacement {
  application?: IApplication;
  can_send_files?: boolean;
  created_at: string;
  declinePending?: boolean;
  decline_reason: null | string;
  decline_reason_id: null | number;
  decline_subreason_id: null | number;
  files_sent?: FileSent[];
  funder: IFunder;
  method: SubmissionMethod;
  offer?: IOffer;
  offers_count: number;
  placement_id: string;
  product: IProduct;
  product_id: ProductType;
  reference_workflow_status: string;
  reference_workflow_sub_status: string;
  reference_workflow_status_color: string;
  responses: ILenderPlacementResponse[];
  status: number;
  tracking: string;
  user: IUser | null;
  workflow_template: IWorkflow | null;
  origin_offer_template_id: string;
  origin_offer_template: { id: string; name: string; short_id: string };
  destinations: {
    workflow_template_id: string;
    workflow_template: IWorkflow;
    offer_template_id: string;
    offer_template: {
      id: string;
      name: string;
    };
  }[];
  url?: string;
}

export interface IFunderPlacement {
  id: number;
  product_id: number;
  method: SubmissionMethod;
  tracking: string;
  status: number;
  decline_reason_id: null | number;
  application: IApplication;
}

export interface OfferFieldsDetails {
  name: string;
  type: FieldType;
  label: string;
  weight: number;
  value: string | number | null;
}

export interface OfferBasicDetails {
  closing: Record<string, unknown>;
  created_at: string;
  id: number;
  offer: Record<string, unknown>;
  offer_id: string;
  offer_template_id: string;
  offer_template: OfferTemplate;
  content: OfferTemplateContent;
  updated_at: string;
}

export enum OfferStatus {
  Accepted = 1,
  Sent,
  Declined,
  Retracted,
  DeclineInClosing,
  RetractedInClosing,
}

export interface IOffer {
  application_id: string;
  contacted_via: Record<string, string>[];
  created_at: string;
  funded_date: string | null;
  funding_advisor_full_name: string;
  id: string;
  placement: ILenderPlacement;
  product: IProduct;
  sent_by: IUser | null;
  status_date: string;
  stips: IStip[];
  declined_reason?: string;
  decline_reason_id?: number;
  declinePending?: boolean;
  funder?: IFunder;
  is_shared: boolean;
  mca_track_id?: string | null;
  placement_id?: ILenderPlacement["placement_id"];
  status?: number;
  tracking?: string;
  offer_details: OfferFieldsDetails[];
  closing_details: OfferFieldsDetails[];
  details: OfferBasicDetails;
  has_closing_details: boolean;
  has_checkout_snapshot: boolean;
}

export type IEquipmentOffer = Omit<
  IOffer,
  | "funding_advisor_full_name"
  | "placement"
  | "stips"
  | "start_date"
  | "end_date"
  | "application"
> & { equipment_name: string; equipment_link: string };

export interface IOfferFundingData {
  max_facility_amount: number | null;
  advance_rate: number | null;
  funding_term_loan_commission: string | null;
  "30_day_discount_rate": number | null;
  "10_day_discount_rate_after_initial_30_days": number | null;
  expense_deposit: number | null;
  origination_fee: number | null;
  renewal: number | null;
  term: number | null;
  term_length?: string | null;
  invoice_amount: number | null;
  draw_amount: number | null;
  funding_amount: number | null;
  start_date: string | null;
  end_date: string | null;
  erc_credit_amount?: number | null;
  erc_filing_date?: string | null;
  erc_total_commission?: number | null;
}

export interface IFunderStatistic {
  general: Record<string, IStatisticsData>;
  products: {
    "1": {
      advance_rate: number;
      "10_day_discount_rate_after_initial_30_days": number;
      "30_day_discount_rate": number;
      term_length: number;
    };
    "2": {
      monthly_interest: number;
      term_length: number;
    };
    "3": {
      max_sell_factor_rate: number;
      min_buy_factor_rate: number;
      term_length: number;
    };
    "4": {
      max_sell_interest_rate: number;
      min_buy_interest_rate: number;
      term_length: number;
    };
    "5": {
      funding_amount: number;
    };
  };
}

export interface IFunderMetric {
  title: string;
  formatter?: (value: number | string) => string | number | null;
  trend?: number;
  data?: IStatisticsData;
  total?: number;
  suffix?: string;
}

export interface IStatistics {
  data: IStatisticsData[];
  total: number;
  title?: string;
  trend?: number;
}

export interface IStatisticsData {
  date: string;
  total: number;
}

export interface IFunderOffersByType {
  "1": IPaginatedResponse<IOffer>;
  "2": IPaginatedResponse<IOffer>;
  "3": IPaginatedResponse<IOffer>;
  "4": IPaginatedResponse<IOffer>;
  "5": IPaginatedResponse<IOffer>;
  "9": IPaginatedResponse<IOffer>;
}

export type PlacementSubmitFunder = IFunder & { productType?: string };

export interface IFundersState {
  meta: IResponseMeta;
  all: IFunder[];
  active: IFunder;
  submissions: IPaginatedResponse<IFunderPlacement>;
  funderOffers: IPaginatedResponse<IOffer>;
  funderOffersByType: IFunderOffersByType;
  activeOffer: IOffer;
  funderUsers: IPaginatedResponse<IUser>;
  funderStatistics: IFunderStatistic;
  hasFunderAdmin: boolean;
}

export interface IPlacementErrorResponse {
  response: {
    data?: {
      data?: {
        message: string;
        placed: boolean;
      };
      message?: string;
      errors?: string[];
    };
  };
}

export type PlacementSettingErrors =
  | typeof PLACEMENT_NO_WFB_TEMPLATE
  | typeof PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE
  | typeof PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE;

export interface PlacementMissingSettingsRecord {
  id: string;
  missing: PlacementSettingErrors[];
  focus?: boolean;
}

export interface GetFundersPayload {
  ids?: string[];
  search?: string;
  risk_tiers?: (string | number)[];
  product_types?: (string | number)[];
  country_id?: (string | number)[];
  area_id?: (string | number)[];
  city_id?: (string | number)[];
  approved?: Booleanish;
  selected_ids?: string[];
  [key: string]: unknown;
}

export interface GroupedOfferSearchParams {
  status?: number;
  origin_template_name?: string;
  offerFunders?: WfbFunderRecord[];
}

export interface PlacementRecordsByField {
  funder: string[];
  product_type: string[];
  submission: number[];
  risk_tier: RiskTier[][];
  dead_reason: (number | null)[];
  placement_status: number[];
  workflow_status: {
    status: string;
    sub_status: string;
    color: string;
  }[];
}

export interface OffersByField {
  funder: string[];
  product_type: string[];
  submission_date: string[];
  application_date: string[];
  funded_date: string[];
}

export interface TasksByField {
  assignee: string[];
  created_by: string[];
  body: string[];
  due_at: string[];
  priority_level: string[];
  status: { status: string; id: number | null }[];
}
